import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link as MuiLink,
  Stack,
  Divider,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import ContactFormDialog from './ContactFormDialog';

interface FooterLink {
  label: string;
  href: string;
  isAnchor?: boolean;
  isExternal?: boolean;
  isContactForm?: boolean;
}

const footerLinks: Record<string, FooterLink[]> = {
  Product: [
    { label: 'Features', href: '/#features', isAnchor: true },
    { label: 'Industries', href: '/#industries', isAnchor: true },
    { label: 'Pricing', href: '/#pricing', isAnchor: true },
    { label: 'FAQ', href: '/#faq', isAnchor: true },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Experts', href: '/experts' },
    { label: 'Contact', href: '#', isContactForm: true },
  ],
  Resources: [
    { label: 'Docs & Help', href: '/docs' },
    { label: 'Status', href: '/status' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'End-User Privacy', href: '/privacy/end-user' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'GDPR', href: '/gdpr' },
    { label: 'Cookie Policy', href: '/cookies' },
  ],
};

// Shared link styling: warm secondary text, copper on hover
const footerLinkSx = {
  fontSize: '0.82rem',
  color: 'text.secondary',
  transition: `color ${tokens.motion.micro}ms ease`,
  '&:hover': { color: tokens.copper },
} as const;

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  const handleClick = (href: string, isAnchor?: boolean) => {
    if (isAnchor) {
      const [path, hash] = href.split('#');

      const tryScroll = (retries = 0) => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (retries < 10) {
          setTimeout(() => tryScroll(retries + 1), 100);
        }
      };

      if (location.pathname === '/' || location.pathname === path) {
        tryScroll();
      } else {
        navigate('/');
        setTimeout(() => tryScroll(), 150);
      }
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        py: 7,
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              component={RouterLink}
              to="/"
              aria-label="CleoLead home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src="/icon-header-clean.png"
                alt="CleoLead"
                sx={{ height: 26, width: 'auto', display: 'block' }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 280 }}>
              The AI Business Manager. Sells, supports, and organizes for your business — 24/7, in your voice, in your control.
            </Typography>
          </Grid>

          {Object.entries(footerLinks).map(([category, links]) => (
            <Grid size={{ xs: 6, sm: 3, md: 2 }} key={category}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  mb: 2,
                  color: tokens.ink,
                }}
              >
                {category}
              </Typography>
              <Stack spacing={1}>
                {links.map((link, index) => {
                  if (link.isContactForm) {
                    return (
                      <MuiLink
                        key={index}
                        component="button"
                        onClick={() => setContactOpen(true)}
                        underline="none"
                        sx={{
                          ...footerLinkSx,
                          cursor: 'pointer',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    );
                  }

                  if (link.isAnchor) {
                    return (
                      <MuiLink
                        key={index}
                        component="button"
                        onClick={() => handleClick(link.href, link.isAnchor)}
                        underline="none"
                        sx={{
                          ...footerLinkSx,
                          cursor: 'pointer',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    );
                  }

                  return (
                    <MuiLink
                      key={index}
                      component={RouterLink}
                      to={link.href}
                      underline="none"
                      sx={footerLinkSx}
                    >
                      {link.label}
                    </MuiLink>
                  );
                })}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: '#D9D0BF' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
            {new Date().getFullYear()} CleoLead. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <MuiLink
              href="mailto:support@cleolead.com"
              underline="none"
              sx={footerLinkSx}
            >
              support@cleolead.com
            </MuiLink>
          </Stack>
        </Box>
      </Container>

      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Contact Us"
        subtitle="Have a question or need help? Send us a message and we'll get back to you within 24 hours."
        formType="general"
      />
    </Box>
  );
}
